import React from 'react';
import { Spin } from 'antd';
import type { Citation } from '../../api/claims/interface';
import useWikiPageHook from '../../data/wiki/useWikiPageHook';
import { useEscDossierContext } from '../DossierContext';
import { processCitations } from '../extractCitedFiles';
import PreviewLayout, { SidebarCard } from '../PreviewLayout/PreviewLayout';
import ReferencesCard from '../ReferencesCard/ReferencesCard';
import { useCitationHandler } from '../utils';
import WikiMarkdown, { headingSlug } from './WikiMarkdown';
import styles from './WikiTab.module.scss';

interface TocEntry {
  level: number;
  text: string;
  slug: string;
}

function extractToc(markdown: string): TocEntry[] {
  const out: TocEntry[] = [];
  for (const line of markdown.split('\n')) {
    const match = line.match(/^(#{2,3}) +([^\n]+)/);
    if (match) {
      const text = match[2].trim();
      out.push({ level: match[1].length, text, slug: headingSlug(text) });
    }
  }
  return out;
}

function parseFrontmatter(markdown: string): {
  meta: Record<string, string>;
  body: string;
} {
  const match = /^---\n([\s\S]*?)\n---\n?/.exec(markdown);
  if (!match) {
    return { meta: {}, body: markdown };
  }
  const meta: Record<string, string> = {};
  for (const line of match[1].split('\n')) {
    const idx = line.indexOf(':');
    if (idx > 0) {
      meta[line.slice(0, idx).trim()] = line
        .slice(idx + 1)
        .trim()
        .replace(/^["']|["']$/g, '');
    }
  }
  return { meta, body: markdown.slice(match[0].length).replace(/^\n+/, '') };
}

interface Props {
  dossierId: string;
  // Wiki-root-relative page path, optionally with a #fragment.
  rawPath: string;
  lead?: React.ReactNode;
}

const WikiPageView: React.FC<Props> = ({ dossierId, rawPath, lead }) => {
  const { onFileClick, onWikiClick, onTaskClick, onClaimClick } =
    useEscDossierContext();
  const hashIdx = rawPath.indexOf('#');
  const path = hashIdx >= 0 ? rawPath.slice(0, hashIdx) : rawPath;
  const fragment = hashIdx >= 0 ? rawPath.slice(hashIdx + 1) : '';
  const [{ page, hasData, status }, handler] = useWikiPageHook(dossierId, path);

  // Fetch once per page; don't retry a page that already resolved or failed.
  React.useEffect(() => {
    if (!hasData && status === undefined) {
      handler.fetchPage();
    }
  }, [hasData, status, handler]);

  // After the target page renders, scroll to the linked #section (if any).
  React.useEffect(() => {
    if (page && fragment) {
      document.getElementById(fragment)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [page, fragment]);

  const onKnowledgeClick = React.useCallback(
    (kbPath: string, anchor?: string) =>
      onWikiClick(`knowledge/${kbPath}${anchor ? `#${anchor}` : ''}`),
    [onWikiClick]
  );
  const onCitationClick = useCitationHandler(onFileClick, onKnowledgeClick) as (
    c: Citation
  ) => void;

  const { meta, body } = React.useMemo(
    () => parseFrontmatter(page?.markdown ?? ''),
    [page?.markdown]
  );
  const { renumberedText, references } = React.useMemo(
    () => processCitations(body),
    [body]
  );
  const toc = React.useMemo(() => extractToc(body), [body]);

  if (!page) {
    if (status === 'rejected') {
      return <div className={styles.error}>This page could not be loaded.</div>;
    }
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  const metaEntries = Object.entries(meta);
  const metaCard =
    metaEntries.length > 0 ? (
      <SidebarCard title="Details">
        <dl className={styles.metaList}>
          {metaEntries.map(([key, value]) => (
            <React.Fragment key={key}>
              <dt className={styles.metaKey}>{key}</dt>
              <dd className={styles.metaVal}>
                {/^https?:\/\//.test(value) ? (
                  <a href={value} target="_blank" rel="noreferrer">
                    {value}
                  </a>
                ) : (
                  value
                )}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      </SidebarCard>
    ) : null;

  const hasSidebar = !!metaCard || toc.length > 0 || references.length > 0;
  const sidebar = hasSidebar ? (
    <>
      {metaCard}
      {toc.length > 0 && (
        <SidebarCard title="On this page">
          <nav className={styles.tocList}>
            {toc.map((entry) => (
              <a
                key={entry.slug}
                href={`#${entry.slug}`}
                className={entry.level === 3 ? styles.tocSub : styles.tocItem}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById(entry.slug)
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {entry.text}
              </a>
            ))}
          </nav>
        </SidebarCard>
      )}
      <ReferencesCard
        references={references}
        onFileClick={onFileClick}
        onKnowledgeClick={onKnowledgeClick}
      />
    </>
  ) : undefined;

  return (
    <PreviewLayout sidebar={sidebar} lead={lead}>
      {page && (
        <WikiMarkdown
          markdown={renumberedText}
          basePath={path}
          onCitationClick={onCitationClick}
          onPageLink={onWikiClick}
          onTaskClick={onTaskClick}
          onClaimClick={onClaimClick}
        />
      )}
    </PreviewLayout>
  );
};

export default WikiPageView;
